import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, HelpCircle, Loader, Server } from "lucide-react";
import Card from "./Card";
import { useApi } from "../hooks/useApi";

/**
 * Ownership triage for discovered endpoints.
 *
 * Discovery can see that an agent calls a remote MCP server; it cannot see who runs it, and
 * guessing from a URL would assert ownership nobody verified. So the question is asked. Until it
 * is answered the endpoint stays a relationship in the graph — which is true either way — and
 * never enters the inventory.
 */

// Every capability class needs an entry. An unmapped class falls back to the read-only grey, which
// would render "data destruction" and "credential access" as the least alarming thing on screen —
// the exact inversion of what they mean.
const CAPABILITY_CHIP = {
  "identity-admin":         "bg-red-50 text-red-700 border border-red-200",
  "financial-transaction":  "bg-red-50 text-red-700 border border-red-200",
  "credential-access":      "bg-red-50 text-red-700 border border-red-200",
  "data-destruction":       "bg-red-50 text-red-700 border border-red-200",
  "shell-execution":        "bg-orange-50 text-orange-700 border border-orange-200",
  "infrastructure-admin":   "bg-orange-50 text-orange-700 border border-orange-200",
  "code-execution":         "bg-orange-50 text-orange-700 border border-orange-200",
  "external-comms":         "bg-orange-50 text-orange-700 border border-orange-200",
  "file-access":            "bg-yellow-50 text-yellow-700 border border-yellow-200",
  "database-access":        "bg-blue-50 text-blue-700 border border-blue-200",
  retrieval:                "bg-blue-50 text-blue-700 border border-blue-200",
  "read-only":              "bg-gray-100 text-gray-600 border border-gray-300",
};

// Only regulated classes colour differently: Public and None are answers, not warnings, and
// colouring them would make classifying something harmless feel like reporting a problem.
const SENSITIVITY_CHIP = {
  PHI:       "bg-red-50 text-red-700 border border-red-200",
  PII:       "bg-red-50 text-red-700 border border-red-200",
  Financial: "bg-red-50 text-red-700 border border-red-200",
  Public:    "bg-gray-100 text-gray-600 border border-gray-300",
  None:      "bg-gray-100 text-gray-600 border border-gray-300",
};

const SENSITIVITIES = ["PHI", "PII", "Financial", "Public", "None"];


function EndpointRow({
  node, reviewedRow, canManage, claiming, setClaiming, ownerUpn, setOwnerUpn, saving, answer,
  classify,
}) {
  const isClaiming = claiming === node.id;
  return (
    <div className="border border-border rounded-md p-2.5 bg-theme-primary">
      <div className="flex flex-wrap items-center gap-2">
        <Server className="w-3.5 h-3.5 text-content-secondary shrink-0" />
        <span className="text-sm font-medium text-content-primary">{node.name}</span>

        {node.capabilityClass && node.capabilityClass !== "unknown" && (
          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
            CAPABILITY_CHIP[node.capabilityClass] || CAPABILITY_CHIP["read-only"]
          }`}>
            {node.capabilityClass}
          </span>
        )}

        {reviewedRow && (
          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
            node.ownershipDisposition === "Operated"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-gray-100 text-gray-600 border border-gray-300"
          }`}>
            {node.ownershipDisposition === "Operated"
              ? node.declaredOwnerUpn || "operated — no owner set"
              : "third-party"}
          </span>
        )}

        {node.dataSensitivity && (
          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${
            SENSITIVITY_CHIP[node.dataSensitivity] || SENSITIVITY_CHIP.None
          }`}>
            {node.dataSensitivity}
          </span>
        )}

        {node.calledBy?.length > 0 && (
          <span className="text-[11px] text-content-secondary">
            called by {node.calledBy.slice(0, 3).join(", ")}
            {node.calledBy.length > 3 && ` +${node.calledBy.length - 3} more`}
          </span>
        )}

        {canManage && (
          <div className="ml-auto flex items-center gap-2">
            {isClaiming ? (
              <>
                <input
                  value={ownerUpn}
                  onChange={(e) => setOwnerUpn(e.target.value)}
                  placeholder="owner@company.com"
                  className="border border-border rounded-md px-2 py-1 text-xs bg-theme-secondary text-content-primary"
                />
                <button
                  onClick={() => answer(node, "Operated", ownerUpn)}
                  disabled={saving === node.id}
                  className="text-xs font-medium text-purple-700 hover:text-purple-900 disabled:opacity-50"
                >
                  {saving === node.id ? <Loader className="w-3 h-3 animate-spin" /> : "Save"}
                </button>
                <button
                  onClick={() => { setClaiming(null); setOwnerUpn(""); }}
                  className="text-xs text-content-secondary hover:text-content-primary"
                >
                  Cancel
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { setClaiming(node.id); setOwnerUpn(node.declaredOwnerUpn || ""); }}
                  className="text-xs font-medium text-purple-700 hover:text-purple-900"
                >
                  {node.ownershipDisposition === "Operated" ? "Change owner" : "We operate this"}
                </button>
                {node.ownershipDisposition !== "ThirdParty" && (
                  <button
                    onClick={() => answer(node, "ThirdParty")}
                    disabled={saving === node.id}
                    className="text-xs text-content-secondary hover:text-content-primary disabled:opacity-50"
                  >
                    Third-party
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {/* Asked in the same sitting as ownership: "who runs this" and "what is in it" are the two
          things a reviewer knows about an endpoint and nobody else does. Offered only where it
          changes something — a read-like endpoint is what the exfiltration rule consults. */}
      {canManage && node.classifiable && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-content-secondary">Data it holds</span>
          <select
            value={node.dataSensitivity ?? ""}
            disabled={saving === node.id}
            onChange={(e) => classify(node, e.target.value)}
            className="text-xs border border-border rounded-md px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
          >
            <option value="">Not assessed</option>
            {SENSITIVITIES.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <span className="text-[11px] text-content-secondary">
            Regulated data raises exfiltration findings to critical on the next scan.
          </span>
        </div>
      )}

      <div className="mt-1 text-[11px] text-content-secondary break-all">{node.nodeKey}</div>
    </div>
  );
}

export default function AiUnclaimedEndpoints({ canManage, onReviewed, refreshKey, onCountChange, onUnavailable, embedded = false }) {
  const apiCall = useApi();
  // True when the request failed or the body was malformed: an unanswered queue is not an empty one.
  const [unavailable, setUnavailable] = useState(false);
  const [pending, setPending] = useState([]);
  const [reviewed, setReviewed] = useState([]);
  const [showReviewed, setShowReviewed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null); // node id being given an owner
  const [ownerUpn, setOwnerUpn] = useState("");
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall("GET", "/ai-assets/endpoints/unclaimed");
      if (!data || !Array.isArray(data.pending)) throw new Error("malformed unclaimed endpoints response");
      setPending(data.pending);
      setReviewed(Array.isArray(data.reviewed) ? data.reviewed : []);
      setUnavailable(false);
      onUnavailable?.(false);
      // Only unanswered endpoints are decisions — reviewed rows are reference, not work.
      onCountChange?.(data.pending.length);
    } catch {
      setPending([]);
      setReviewed([]);
      setUnavailable(true);
      onUnavailable?.(true);
      onCountChange?.(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-read whenever a scan completes: discovery is the only thing that adds endpoints to the
  // queue, so without this the card sits stale until the page is reloaded.
  useEffect(() => { load(); }, [load, refreshKey]);

  const classify = async (node, sensitivity) => {
    setSaving(node.id);
    try {
      await apiCall("PUT", `/ai-assets/endpoints/${node.id}/sensitivity`, {
        dataSensitivity: sensitivity || null,
      });
      // Re-read for the same reason ownership does: this is a graph fact the rest of the page
      // renders, and patching one row locally leaves the others describing the previous answer.
      await load();
      onReviewed?.();
    } finally {
      setSaving(null);
    }
  };

  const answer = async (node, disposition, upn) => {
    setSaving(node.id);
    try {
      await apiCall("PUT", `/ai-assets/endpoints/${node.id}/ownership`, {
        disposition,
        ownerUpn: upn || null,
      });
      setClaiming(null);
      setOwnerUpn("");
      // Re-read rather than patching locally: an answer moves a row between the two lists, and
      // changing one can create or stale an asset the rest of the page is showing.
      await load();
      onReviewed?.();
    } finally {
      setSaving(null);
    }
  };

  if (!loading && unavailable) {
    return (
      <p className="text-sm text-content-secondary" data-testid="unclaimed-unavailable">
        Endpoints waiting for an ownership answer are unavailable right now.
      </p>
    );
  }

  // Nothing discovered that anyone could be asked about.
  if (loading || (pending.length === 0 && reviewed.length === 0)) return null;

  const rowProps = {
    canManage, claiming, setClaiming, ownerUpn, setOwnerUpn, saving, answer, classify,
  };

  // Embedded in the Action Center: the card lives on the container; the explanatory header
  // stays — it is the copy that tells someone why answering matters.
  const Wrapper = embedded
    ? ({ children }) => <div>{children}</div>
    : ({ children }) => (
        <Card className={`p-4 mb-4 ${pending.length > 0 ? "border-l-4 border-l-yellow-400" : ""}`}>
          {children}
        </Card>
      );

  return (
    <Wrapper>
      {pending.length > 0 ? (
        <div className="flex items-start gap-2 mb-3">
          <HelpCircle className="w-4 h-4 mt-0.5 text-yellow-600 shrink-0" />
          <div>
            <div className="text-sm font-semibold text-content-primary">
              {pending.length} endpoint{pending.length === 1 ? " needs" : "s need"} an owner
            </div>
            <div className="text-xs text-content-secondary">
              Your agents call {pending.length === 1 ? "this service" : "these services"}. Axaris
              cannot tell from the address whether you run{" "}
              {pending.length === 1 ? "it" : "them"} — telling us adds{" "}
              {pending.length === 1 ? "it" : "them"} to your inventory, and marking{" "}
              {pending.length === 1 ? "it" : "them"} third-party flags the calls as data leaving
              your organization.
            </div>
          </div>
        </div>
      ) : (
        <div className="text-sm font-semibold text-content-primary mb-1">
          Discovered endpoints
        </div>
      )}

      <div className="space-y-2">
        {pending.map((node) => <EndpointRow key={node.id} node={node} {...rowProps} />)}
      </div>

      {reviewed.length > 0 && (
        <div className={pending.length > 0 ? "mt-3" : ""}>
          <button
            onClick={() => setShowReviewed((v) => !v)}
            className="flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary"
          >
            {showReviewed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {reviewed.length} reviewed endpoint{reviewed.length === 1 ? "" : "s"}
          </button>
          {showReviewed && (
            <div className="mt-2 space-y-2">
              {reviewed.map((node) => (
                <EndpointRow key={node.id} node={node} reviewedRow {...rowProps} />
              ))}
            </div>
          )}
        </div>
      )}
    </Wrapper>
  );
}
